import {useMemo, useState} from 'react'
import {Image, Linking, Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {toNiceDomain} from '#/lib/strings/url-helpers'
import {DraggableScrollView} from '#/view/com/pager/DraggableScrollView'
import {atoms as a, useTheme} from '#/alf'
import {EditBig_Stroke2_Corner0_Rounded as ComposeIcon} from '#/components/icons/EditBig'
import {Earth_Stroke2_Corner0_Rounded as EarthIcon} from '#/components/icons/Globe'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {type Trend, type TrendCard} from '../data/trends'
import {trendHashtag} from '../util'
import {EarthFilledIcon} from './EarthFilledIcon'
import {ImageWithPlaceholder} from './ImageWithPlaceholder'

/**
 * Horizontal carousel of external news cards rendered directly under the
 * `TrendSearchHeader`. Mirrors Figma node `442:6809` in file
 * `C8J3bA4ThNVCG0vzlHeI16`.
 *
 * Card anatomy: image, source row (favicon + name), headline + summary, and a
 * domain row below a hairline divider. The favicon is derived from the article
 * URL's host; the source name comes from `card.sourceName`.
 *
 * Below each card a footer holds a "Create post" button that opens the composer
 * referencing the article (its URL becomes a link embed) with the trend's
 * hashtag added under the link. The Figma's active-voices avatars are omitted —
 * we don't have per-card engagement yet.
 */
export function FeaturedCardsCarousel({
  cards,
  trend,
}: {
  cards: TrendCard[]
  trend: Trend
}) {
  const hashtag = trendHashtag(trend)
  if (cards.length === 0) return null
  return (
    <DraggableScrollView
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[
        a.flex_row,
        // Stretch every card to the tallest one's height so the divider + domain
        // row can be pinned to the bottom and line up across cards.
        {
          alignItems: 'stretch',
          paddingLeft: 16,
          paddingRight: 16,
          paddingBottom: 32,
          gap: 8,
        },
      ]}>
      {cards.map(card => (
        <FeaturedCard
          key={card.id}
          card={card}
          title={trend.title}
          hashtag={hashtag}
        />
      ))}
    </DraggableScrollView>
  )
}

/** Host of a URL, or undefined when it's missing / unparseable. */
function hostForUrl(url: string): string | undefined {
  try {
    return new URL(url).host || undefined
  } catch {
    return undefined
  }
}

/**
 * Format an article's publish time for the source row (Figma 442:6809):
 *   - same calendar day  → "2h" (whole hours since publish, min "1h")
 *   - the day before     → "Yesterday" (localized; caller passes the label)
 *   - older              → "3 Jun" (day-first, localized short month)
 *
 * Returns undefined for a missing / unparseable timestamp so the row can hide
 * the time. `iso` is expected to be an ISO 8601 string (RSS pubDate).
 */
function formatCardTimestamp(
  iso: string | undefined,
  locale: string,
  yesterdayLabel: string,
): string | undefined {
  if (!iso) return undefined
  const date = new Date(iso)
  const ms = date.getTime()
  if (Number.isNaN(ms)) return undefined

  const now = new Date()
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime()
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime()
  const dayDiff = Math.round((startOfToday - startOfDate) / 86_400_000)

  if (dayDiff <= 0) {
    const hours = Math.max(1, Math.floor((now.getTime() - ms) / 3_600_000))
    return `${hours}h`
  }
  if (dayDiff === 1) return yesterdayLabel
  try {
    const month = new Intl.DateTimeFormat(locale, {month: 'short'}).format(date)
    return `${date.getDate()} ${month}`
  } catch {
    return `${date.getDate()}`
  }
}

function FeaturedCard({
  card,
  title,
  hashtag,
}: {
  card: TrendCard
  /** The trend's title, included verbatim so the post surfaces under it. */
  title: string
  hashtag: string
}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_, i18n} = useLingui()
  const {openComposer} = useOpenComposer()

  // Published time shown next to the source name: "2h" today, "Yesterday", or
  // "3 Jun" for older. Empty until the backend sends `publishedAt` (RSS pubDate).
  const publishedLabel = useMemo(
    () => formatCardTimestamp(card.publishedAt, i18n.locale, _(msg`Yesterday`)),
    [card.publishedAt, i18n.locale, _],
  )

  // Image / icon fallback hierarchy:
  //   1. article image                  → drawn over the placeholder below and
  //                                        only shown once loaded, so a slow or
  //                                        blocked publisher leaves 2/3 visible
  //   2. no image, apple-touch-icon     → that icon, full-bleed (image area)
  //   3. no image, no touch icon        → globe tile
  //   4. no/unresolvable link           → globe tile + "unknown.com"
  // Display (favicon / domain / touch-icon) uses the publisher's `sourceUrl`
  // when present — `card.url` is a `news.google.com/rss/…` redirect, so it
  // would otherwise show "news.google.com" instead of the real source. Falls
  // back to `card.url` until the backend supplies `sourceUrl`. The tap target
  // and "Create post" link still use `card.url` (it redirects to the article).
  const displayUrl = card.sourceUrl ?? card.url
  const host = hostForUrl(displayUrl)
  const favicon = host
    ? `https://www.google.com/s2/favicons?sz=128&domain=${host}`
    : undefined
  const [faviconFailed, setFaviconFailed] = useState(false)
  const showFavicon = !!favicon && !faviconFailed
  const domainLabel = host ? toNiceDomain(displayUrl) : 'unknown.com'

  // Image-area fallback only: probe the site's apple-touch-icon (a crisp,
  // full-bleed app icon) at its well-known root paths. We don't parse the
  // page's <link> tags, so a custom path won't be found — that's fine, we just
  // fall through to the globe. The small favicon in the source row is separate
  // and unaffected.
  const appleTouchIcons = useMemo(() => {
    if (!host) return []
    let origin: string
    try {
      origin = new URL(displayUrl).origin
    } catch {
      return []
    }
    return [
      `${origin}/apple-touch-icon.png`,
      `${origin}/apple-touch-icon-precomposed.png`,
    ]
  }, [host, displayUrl])
  const [appleIconIdx, setAppleIconIdx] = useState(0)
  const appleTouchIcon = appleTouchIcons[appleIconIdx]

  const openCard = () => {
    // External news article — hand off to the OS handler. When in-app browser
    // wiring exists for this screen, switch to it here.
    Linking.openURL(card.url).catch(() => {
      // Swallow — there's no UX to fall back to, and dev console already logs.
    })
  }

  // Open the composer referencing this article — its URL in the text becomes a
  // link-card embed. The URL sits two lines down so the user has room to write
  // above it; the editor puts the cursor at the start when the initial text
  // begins with a newline (see TextInput.web.tsx).
  // Prefill: blank lines (cursor starts at top so the user writes their own
  // words first), then the trend title, the article link, and the trend
  // hashtag under it. The title is included verbatim for now: the posts listed
  // under a trend come from a search on `trend.title` (see useTrendPosts), so
  // a post without the full title never shows up there.
  const onCreatePost = () =>
    openComposer({text: `\n\n${title}\n\n${card.url}\n\n${hashtag}`})

  // Image-area placeholder: the site's apple-touch-icon, else a globe tile.
  // Also drawn underneath a real image (see ImageWithPlaceholder).
  const imageAreaFallback = (
    <View
      style={[
        a.w_full,
        a.h_full,
        a.align_center,
        a.justify_center,
        {backgroundColor: ss.bg.contrast_50},
      ]}>
      {appleTouchIcon ? (
        <Image
          accessibilityIgnoresInvertColors
          source={{uri: appleTouchIcon}}
          onError={() => setAppleIconIdx(i => i + 1)}
          style={{width: 64, height: 64, borderRadius: 16}}
          resizeMode="cover"
        />
      ) : (
        <View
          style={[
            a.align_center,
            a.justify_center,
            {
              width: 64,
              height: 64,
              borderRadius: 16,
              backgroundColor: ss.bg.contrast_25,
            },
          ]}>
          <EarthFilledIcon size="2xl" />
        </View>
      )}
    </View>
  )

  return (
    <View style={[{width: 315, gap: 8}]}>
      <Pressable
        accessibilityRole="link"
        accessibilityLabel={`${card.sourceName}: ${card.headline}`}
        accessibilityHint={_(msg`Opens the article in your browser`)}
        onPress={openCard}
        style={[
          a.w_full,
          a.flex_1,
          {
            borderRadius: 24,
            overflow: 'hidden',
            backgroundColor: ss.bg.contrast_25,
          },
        ]}>
        {card.image ? (
          <ImageWithPlaceholder
            uri={card.image.url}
            style={[a.w_full, {aspectRatio: 1240 / 698}]}
            placeholder={imageAreaFallback}
          />
        ) : (
          // Standalone, the fallback needs the same box the image would have
          // had (it only fills its parent).
          <View style={[a.w_full, {aspectRatio: 1240 / 698}]}>
            {imageAreaFallback}
          </View>
        )}

        {/* Source: favicon + provider name + published time. The name truncates
          (flexShrink) so the time stays visible (Figma 442:6809). */}
        {(!!card.sourceName || !!publishedLabel) && (
          <View
            style={[
              a.flex_row,
              a.align_center,
              {paddingHorizontal: 20, paddingTop: 12, paddingBottom: 8, gap: 6},
            ]}>
            {showFavicon && (
              <Image
                accessibilityIgnoresInvertColors
                source={{uri: favicon}}
                onError={() => setFaviconFailed(true)}
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 6,
                  backgroundColor: ss.bg.default,
                }}
              />
            )}
            {!!card.sourceName && (
              <Text
                numberOfLines={1}
                style={[
                  a.text_sm,
                  a.font_medium,
                  // Without a leading atom the web normalizer sets
                  // lineHeight === fontSize, and numberOfLines clips the box —
                  // so descenders ("google", "Aftonbladet") lose their tails.
                  a.leading_snug,
                  t.atoms.text_contrast_medium,
                  {flexShrink: 1},
                ]}>
                {card.sourceName}
              </Text>
            )}
            {!!publishedLabel && (
              <Text
                numberOfLines={1}
                style={[
                  a.text_sm,
                  a.leading_snug,
                  t.atoms.text_contrast_medium,
                  {flexShrink: 0},
                ]}>
                {card.sourceName ? `· ${publishedLabel}` : publishedLabel}
              </Text>
            )}
          </View>
        )}

        {/* Headline + summary — flexes so the divider + domain row below pins to
          the bottom and lines up across cards of different text lengths. */}
        <View
          style={[
            a.flex_1,
            a.gap_sm,
            {paddingHorizontal: 20, paddingBottom: 12},
          ]}>
          <Text
            style={[a.text_md, a.font_medium, a.leading_snug, t.atoms.text]}
            numberOfLines={3}>
            {card.headline}
          </Text>
          {card.summary ? (
            <Text
              style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}
              numberOfLines={2}>
              {card.summary}
            </Text>
          ) : null}
        </View>

        {/* Domain row, below a hairline divider */}
        <View
          style={[
            a.flex_row,
            a.align_center,
            a.border_t,
            {
              borderColor: ss.border.contrast_low,
              paddingHorizontal: 20,
              paddingTop: 12,
              paddingBottom: 16,
              gap: 5,
            },
          ]}>
          <EarthIcon size="xs" fill={t.atoms.text_contrast_medium.color} />
          <Text
            numberOfLines={1}
            style={[
              a.flex_1,
              a.text_xs,
              a.leading_snug,
              t.atoms.text_contrast_medium,
            ]}>
            {domainLabel}
          </Text>
        </View>
      </Pressable>

      {/* Footer: "Create post" referencing the article. Left-aligned — there
          are no voices/avatars to sit opposite it on the right. */}
      <View
        style={[
          a.flex_row,
          a.justify_start,
          a.align_center,
          {paddingLeft: 4, paddingRight: 12, paddingVertical: 8},
        ]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={_(msg`Create post about this article`)}
          accessibilityHint={_(msg`Opens the composer with the article linked`)}
          onPress={onCreatePost}
          style={[
            a.flex_row,
            a.align_center,
            a.justify_center,
            a.rounded_full,
            a.border,
            {
              gap: 8,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderColor: ss.border.contrast_low,
            },
          ]}>
          <ComposeIcon size="sm" fill={t.atoms.text_contrast_medium.color} />
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            <Trans>Create post</Trans>
          </Text>
        </Pressable>
      </View>
    </View>
  )
}
